import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Showtime } from '../models';
import { CompactShowtimePillComponent } from './compact-showtime-pill.component';

export interface MovieShowtimeGroup {
  movie: string;
  posterUrl?: string;
  language?: string;
  runtimeMinutes?: number;
  items: Showtime[];
}

@Component({
  selector: 'app-movie-showtime-group',
  standalone: true,
  imports: [CommonModule, CompactShowtimePillComponent],
  template: `
    <article class="movie-times" *ngIf="group">
      <div class="poster-thumb">
        <img *ngIf="group.posterUrl" [src]="group.posterUrl" [alt]="group.movie + ' poster'" loading="lazy" />
        <span *ngIf="!group.posterUrl">{{ initials(group.movie) }}</span>
      </div>

      <div class="movie-main">
        <div class="movie-line">
          <div>
            <h2>{{ group.movie }}</h2>
            <p>
              <span *ngIf="group.language">{{ group.language }}</span>
              <span *ngIf="group.runtimeMinutes">{{ group.runtimeMinutes }} min</span>
            </p>
          </div>
          <span class="count">{{ group.items.length }}</span>
        </div>

        <div class="time-list">
          <app-compact-showtime-pill *ngFor="let showtime of group.items" [showtime]="showtime" />
        </div>
      </div>
    </article>
  `,
  styles: [
    `
      .movie-times {
        display: grid;
        grid-template-columns: 52px minmax(0, 1fr);
        gap: 10px;
        border: 1px solid var(--line);
        border-radius: 15px;
        padding: 10px;
        background: var(--surface-row);
        box-shadow: 0 10px 28px rgba(0, 0, 0, 0.18);
      }

      .poster-thumb {
        width: 52px;
        aspect-ratio: 2 / 3;
        border: 1px solid var(--poster-border);
        border-radius: 11px;
        overflow: hidden;
        display: grid;
        place-items: center;
        color: var(--text);
        background: linear-gradient(135deg, var(--blue), var(--violet));
        font-size: 13px;
        font-weight: 950;
      }

      .poster-thumb img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .movie-main {
        min-width: 0;
      }

      .movie-line {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        margin-bottom: 8px;
      }

      h2 {
        margin: 0 0 3px;
        color: var(--text);
        font-size: 15px;
        line-height: 1.16;
        font-weight: 950;
        overflow-wrap: anywhere;
      }

      p {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
        margin: 0;
      }

      p span {
        color: var(--muted);
        font-size: 11px;
        line-height: 1.2;
        font-weight: 800;
      }

      .count {
        min-width: 30px;
        min-height: 24px;
        border-radius: 999px;
        padding: 4px 8px;
        display: grid;
        place-items: center;
        color: var(--cyan);
        background: var(--control-bg);
        font-size: 11px;
        font-weight: 950;
      }

      .time-list {
        display: flex;
        flex-wrap: wrap;
        gap: 7px;
      }

      @media (max-width: 640px) {
        .movie-times {
          grid-template-columns: 46px minmax(0, 1fr);
          gap: 9px;
          padding: 9px;
          border-radius: 14px;
        }

        .poster-thumb {
          width: 46px;
          border-radius: 10px;
          font-size: 12px;
        }

        h2 {
          font-size: 14px;
        }

        .time-list {
          gap: 6px;
        }
      }
    `,
  ],
})
export class MovieShowtimeGroupComponent {
  @Input({ required: true }) group!: MovieShowtimeGroup;

  initials(title: string): string {
    return title.split(/\s+/).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
  }
}
